import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Form } from 'src/registry/ui/Form';
import { commonMessages } from 'src/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS, CURRENCY_MAP } from 'src/features/moneyAccounts/utils/constants';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { CURRENCY } from 'src/types';
import React from 'react';
import { useListBillingEntities } from 'src/features/billing/api/useListBillingEntities';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import { BankAccountFormSchemaValues } from 'src/features/billingEntityBankAccounts/utils/formSchema';
import { bankAccountMessages } from 'src/features/billingEntityBankAccounts/utils/messages';
import { billingMessages } from 'src/features/billing/utils/messages';
import { moneyAccountsMessages } from 'src/features/moneyAccounts/utils/messages';
import { useParams } from 'react-router-dom';

type BillingEntityBankAccountFormProps = {
    form: UseFormReturn<BankAccountFormSchemaValues>;
    onSubmit: (values: BankAccountFormSchemaValues) => void;
    isEditMode?: boolean;
    isDraft?: boolean;
};

function BillingEntityBankAccountForm({
    onSubmit,
    form,
    isEditMode = false,
    isDraft,
}: BillingEntityBankAccountFormProps) {
    const { formatMessage } = useIntl();
    const { entityId } = useParams();

    const onBillingEntityDashboard = Boolean(entityId);

    const [searchBillingEntity, setSearchBillingEntity] = React.useState<string>('');

    const { data: billingEntities } = useListBillingEntities({
        search: searchBillingEntity,
    });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<BankAccountFormSchemaValues>
                form={form}
                name="name"
                type="text"
                label={formatMessage(commonMessages.name)}
            />
            <InputFormField<BankAccountFormSchemaValues>
                form={form}
                name="iban"
                type="text"
                label={formatMessage(bankAccountMessages.iban)}
            />
            <InputFormField<BankAccountFormSchemaValues>
                form={form}
                name="bic"
                type="text"
                label={formatMessage(bankAccountMessages.bic)}
            />

            <SelectFormField<BankAccountFormSchemaValues>
                form={form}
                name="currency"
                required
                label={formatMessage(commonMessages.currency)}
                options={Object.values(CURRENCY)}
                optionTranslationMap={CURRENCY_MAP}
                isDisabled={isEditMode && !isDraft}
            />
            <SearchSelectFormField
                name="billing_entity"
                control={form.control}
                label={formatMessage(billingMessages.billingEntity)}
                items={billingEntities?.results ?? []}
                clearable
                placeholder={`${formatMessage(billingMessages.billingEntity)}...`}
                searchPlaceholder={formatMessage(billingMessages.searchBillingEntity)}
                emptyTitle={formatMessage(billingMessages.noBillingEntity)}
                emptyDescription={formatMessage(billingMessages.noBillingEntityFound)}
                getItemValue={(entity) => entity.id}
                getItemLabel={(entity) => entity.name}
                getItemKeywords={(entity) => [entity.name]}
                loadOptions={setSearchBillingEntity}
                disabled={onBillingEntityDashboard}
            />
            <SelectFormField<BankAccountFormSchemaValues>
                form={form}
                name="is_active"
                isBoolean={true}
                label={formatMessage(moneyAccountsMessages.isActive)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
        </Form>
    );
}

export default BillingEntityBankAccountForm;
